/**
 * Data layer for `advertiser-portal`. Thin typed fetch wrappers over
 * /api/v1/advertiser-portal. Raw `fetch()` is confined to this file per the
 * dash contract (D6).
 *
 * All requests send `x-advertiser-id` — the backend returns
 * `{error:"advertiser_required"}` (400) if missing. Dash stores the current
 * advertiser id in localStorage under the `trackerity.advertiser_id` key; the
 * page uses an input to let the user enter one.
 */

export type ConversionStatus = "pending" | "approved" | "rejected" | "refunded";

export interface ConversionView {
	id: string;
	advertiser_id: string;
	campaign_id: string;
	amount_cents: number;
	currency: string;
	converted_at: number;
	click_id: string;
	status: ConversionStatus;
}

export interface ConversionRange {
	from: number;
	to: number;
}

export interface ListConversionsResponse {
	advertiser_id: string;
	range: ConversionRange;
	campaign_id: string | null;
	limit: number;
	count: number;
	next_cursor: string | null;
	conversions: ConversionView[];
}

export interface DailyRollupRow {
	day: string;
	count: number;
	revenue_cents: number;
}

export interface DailyReportResponse {
	advertiser_id: string;
	range: ConversionRange;
	total_conversions: number;
	total_revenue_cents: number;
	days: DailyRollupRow[];
}

export interface CampaignAggregateRow {
	campaign_id: string;
	count: number;
	revenue_cents: number;
}

export interface CampaignReportResponse {
	advertiser_id: string;
	range: ConversionRange;
	total_campaigns: number;
	total_conversions: number;
	total_revenue_cents: number;
	campaigns: CampaignAggregateRow[];
}

export interface ListConversionsArgs {
	advertiserId: string;
	from?: number;
	to?: number;
	campaignId?: string;
	limit?: number;
	cursor?: string;
}

export interface ReportArgs {
	advertiserId: string;
	from?: number;
	to?: number;
}

const BASE = "/api/v1/advertiser-portal";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

function advertiserHeaders(advertiserId: string): Record<string, string> {
	return {
		accept: "application/json",
		"x-advertiser-id": advertiserId,
	};
}

function qs(params: Record<string, string | number | undefined>): string {
	const parts: string[] = [];
	for (const [k, v] of Object.entries(params)) {
		if (v === undefined || v === null || v === "") continue;
		parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
	}
	return parts.length > 0 ? `?${parts.join("&")}` : "";
}

export async function listConversions(
	args: ListConversionsArgs,
): Promise<ListConversionsResponse> {
	const query = qs({
		from: args.from,
		to: args.to,
		campaign_id: args.campaignId,
		limit: args.limit,
		cursor: args.cursor,
	});
	const res = await fetch(`${BASE}/conversions${query}`, {
		method: "GET",
		credentials: "include",
		headers: advertiserHeaders(args.advertiserId),
	});
	return parseOrThrow<ListConversionsResponse>(res);
}

export async function getDailyReport(args: ReportArgs): Promise<DailyReportResponse> {
	const query = qs({ from: args.from, to: args.to });
	const res = await fetch(`${BASE}/reports/daily${query}`, {
		method: "GET",
		credentials: "include",
		headers: advertiserHeaders(args.advertiserId),
	});
	return parseOrThrow<DailyReportResponse>(res);
}

export async function getCampaignReport(
	args: ReportArgs,
): Promise<CampaignReportResponse> {
	const query = qs({ from: args.from, to: args.to });
	const res = await fetch(`${BASE}/reports/campaign${query}`, {
		method: "GET",
		credentials: "include",
		headers: advertiserHeaders(args.advertiserId),
	});
	return parseOrThrow<CampaignReportResponse>(res);
}

export const advertiserPortalQueryKeys = {
	all: ["advertiser-portal"] as const,
	conversionsList: (args: ListConversionsArgs) =>
		[...advertiserPortalQueryKeys.all, "conversions", args] as const,
	dailyReport: (args: ReportArgs) =>
		[...advertiserPortalQueryKeys.all, "daily", args] as const,
	campaignReport: (args: ReportArgs) =>
		[...advertiserPortalQueryKeys.all, "campaign", args] as const,
};
